/**
 * HelpIndexPage — /help
 *
 * Search bar, category grid, popular articles.
 * Matches the PublicLayout warm-paper palette.
 */
import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SEOHead } from "@/components/seo/SEOHead.tsx";
import {
  HELP_CATEGORIES,
  HELP_ARTICLES,
  POPULAR_ARTICLE_SLUGS,
  searchArticles,
  getArticlesByCategory,
} from "@/help/articles.ts";

const T = {
  ink: "#0a0a0a",
  inkSoft: "#6b6b6b",
  rule: "#e9e6df",
  paper: "#faf8f3",
  paper2: "#f3efe5",
  accent: "#ff5b1f",
};

// ── Inline SVG icons (small, used only in this file) ─────────────────────────

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.inkSoft} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" />
    </svg>
  );
}

function CategoryIcon({ name }: { name: string }) {
  const props = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const icons: Record<string, React.ReactNode> = {
    sparkle:    <><path d="M12 3v6M12 15v6M3 12h6M15 12h6" /></>,
    video:      <><rect x="3" y="6" width="14" height="12" rx="2" /><path d="M17 10l4-2v8l-4-2z" /></>,
    layers:     <><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 13l9 5 9-5" /></>,
    youtube:    <><rect x="2" y="6" width="20" height="12" rx="3" /><polygon points="10 9 16 12 10 15" fill="currentColor" stroke="none" /></>,
    image:      <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></>,
    stats:      <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
    creditCard: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>,
    settings:   <><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.5-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.4 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.5 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.5 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.4-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.5-2-1.5c.1-.4.1-.8.1-1.2z" /></>,
    help:       <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4" /><circle cx="12" cy="17" r="0.5" fill="currentColor" /></>,
  };
  return <svg {...props}>{icons[name] ?? icons.help}</svg>;
}

function ChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

// ── Help FAQ JSON-LD ──────────────────────────────────────────────────────────

function helpFaqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is TubeForge?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "TubeForge is an AI-powered platform that creates faceless YouTube videos from a topic idea — handling script, storyboard, voiceover, captions, and publishing automatically.",
        },
      },
      {
        "@type": "Question",
        name: "How do credits work?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Credits are TubeForge's usage currency. Script generation costs 5 credits, video rendering costs 100 credits, and image regeneration costs 5 credits per frame. Monthly credits are included with your plan.",
        },
      },
      {
        "@type": "Question",
        name: "Which plans allow YouTube publishing?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "YouTube publishing is available on Starter, Pro, and Agency plans. Scheduled publishing requires Pro or Agency.",
        },
      },
    ],
  };
}

// ── Search results ────────────────────────────────────────────────────────────

function SearchResults({ query }: { query: string }) {
  const results = searchArticles(query);

  if (results.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0" }}>
        <p style={{ fontSize: 15, color: T.inkSoft }}>
          No results for <strong>"{query}"</strong>. Try a different search term.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: T.inkSoft, marginBottom: 16 }}>
        {results.length} result{results.length !== 1 ? "s" : ""} for "{query}"
      </p>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        {results.map((article) => {
          const cat = HELP_CATEGORIES.find((c) => c.slug === article.category);
          return (
            <li key={article.slug}>
              <Link
                to={`/help/${article.slug}`}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderRadius: 10, background: "#fff", border: `1px solid ${T.rule}`, textDecoration: "none", color: T.ink, transition: "border-color 0.15s" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = T.accent; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = T.rule; }}
              >
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{article.title}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: T.inkSoft }}>{cat?.label}</p>
                </div>
                <span style={{ color: T.inkSoft, flexShrink: 0 }}><ChevronRight /></span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function HelpIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");

  // Keep input in sync if the URL changes externally (back/forward navigation)
  useEffect(() => {
    setInputValue(searchParams.get("q") ?? "");
  }, [searchParams]);

  const query = searchParams.get("q")?.trim() ?? "";
  const isSearching = query.length > 0;

  function handleInputChange(value: string) {
    setInputValue(value);
    const trimmed = value.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }

  function handleCategoryClick(label: string) {
    setInputValue(label);
    setSearchParams({ q: label }, { replace: true });
  }

  const popularArticles = POPULAR_ARTICLE_SLUGS
    .map((slug) => HELP_ARTICLES.find((a) => a.slug === slug))
    .filter(Boolean) as typeof HELP_ARTICLES;

  return (
    <>
      <SEOHead
        title="Help Center"
        description="Find answers to common questions about TubeForge — videos, billing, channels, series, and more."
        path="/help"
        jsonLd={helpFaqJsonLd()}
      />

      {/* Hero */}
      <section
        style={{ backgroundColor: T.paper2, borderBottom: `1px solid ${T.rule}`, padding: "64px 20px 48px" }}
      >
        <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <h1
            style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, margin: "0 0 8px", fontFamily: '"Bricolage Grotesque", "Inter", system-ui', color: T.ink }}
          >
            TubeForge Help Center
          </h1>
          <p style={{ fontSize: 16, color: T.inkSoft, marginBottom: 32 }}>
            Guides and answers for everything in TubeForge.
          </p>

          {/* Search */}
          <div
            style={{ position: "relative", maxWidth: 480, margin: "0 auto" }}
            role="search"
          >
            <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <SearchIcon />
            </span>
            <input
              type="search"
              placeholder="Search articles…"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              aria-label="Search help articles"
              style={{ width: "100%", padding: "13px 16px 13px 46px", fontSize: 15, border: `1px solid ${T.rule}`, borderRadius: 12, background: "#fff", color: T.ink, fontFamily: "inherit", outline: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
              onFocus={(e) => { e.currentTarget.style.borderColor = T.accent; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = T.rule; }}
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "48px 20px 80px" }}>
        {isSearching ? (
          <SearchResults query={query} />
        ) : (
          <>
            {/* Category grid */}
            <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.inkSoft, marginBottom: 20 }}>
              Browse by topic
            </h2>
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12, marginBottom: 56 }}
            >
              {HELP_CATEGORIES.map((cat) => {
                const count = getArticlesByCategory(cat.slug).length;
                return (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.label)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "18px 20px", borderRadius: 12, background: "#fff", border: `1px solid ${T.rule}`, textDecoration: "none", color: T.ink, transition: "border-color 0.15s, box-shadow 0.15s", cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = T.accent; el.style.boxShadow = "0 4px 14px rgba(0,0,0,0.07)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = T.rule; el.style.boxShadow = "none"; }}
                  >
                    <span style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: T.paper2, display: "flex", alignItems: "center", justifyContent: "center", color: T.accent }}>
                      <CategoryIcon name={cat.icon} />
                    </span>
                    <div>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.ink }}>{cat.label}</p>
                      <p style={{ margin: "3px 0 0", fontSize: 12, color: T.inkSoft, lineHeight: 1.4 }}>{cat.description}</p>
                      <p style={{ margin: "6px 0 0", fontSize: 11, color: T.accent, fontWeight: 600 }}>{count} article{count !== 1 ? "s" : ""}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Popular articles */}
            <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.inkSoft, marginBottom: 20 }}>
              Popular articles
            </h2>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              {popularArticles.map((article) => {
                const cat = HELP_CATEGORIES.find((c) => c.slug === article.category);
                return (
                  <li key={article.slug}>
                    <Link
                      to={`/help/${article.slug}`}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderRadius: 10, background: "#fff", border: `1px solid ${T.rule}`, textDecoration: "none", color: T.ink, transition: "border-color 0.15s" }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = T.accent; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = T.rule; }}
                    >
                      <div>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{article.title}</p>
                        <p style={{ margin: "2px 0 0", fontSize: 12, color: T.inkSoft }}>{cat?.label}</p>
                      </div>
                      <span style={{ color: T.inkSoft, flexShrink: 0 }}><ChevronRight /></span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
    </>
  );
}
